import {
  useDeletePracticeMutation,
  useGetPracticesQuery,
  useUpdatePracticeMutation,
} from "api";
import useTitle from "hooks/useTitle";
import {
  Table,
  Tag,
  Toggle,
  PencilIcon,
  TrashIcon,
  ConfirmationModal,
  NotifierNotification,
  Input,
  SearchIcon,
  Select,
  SelectOption,
  SortIcon,
  ErrorMessage,
  Filter,
  FilterIcon,
  Button,
} from "@mwi/ui";
import { useEffect, useMemo, useState } from "react";
import NewPracticeModal from "components/modals/admin/practice/new";
import EditPracticeModal from "components/modals/admin/practice/edit";
import { useNotifier } from "react-headless-notifier";
import { useDispatch, useSelector } from "react-redux";
import { setFilters, setSorting } from "slices/practiceManagementFilters";
import { useForm } from "react-hook-form";
import useSearchQuery from "hooks/useSearchQuery";
import { RootState } from "store";
import SortBy from "types/practices/SortBy";
import { useDebounce } from "use-debounce";

type Inputs = {
  status: string | undefined;
};

const PracticesPage = (): JSX.Element => {
  useTitle("Practices");

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const { notify } = useNotifier();
  const dispatch = useDispatch();

  const [showNewPracticeModal, setShowNewPracticeModal] = useState(false);
  const [editingPractice, setEditingPractice] = useState<number>();
  const [deletingPractice, setDeletingPractice] = useState<number>();
  const [searchTerm, setSearchTerm] = useState<string | undefined>(
    params.get("search") ?? undefined,
  );
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    params.get("status") ?? undefined,
  );
  const [sort, setSort] = useState<SortBy | undefined>(
    (params.get("sort") ?? undefined) as SortBy,
  );

  const [debouncedSearchTerm] = useDebounce(searchTerm, 400);

  const [updatePractice] = useUpdatePracticeMutation();
  const [
    deletePractice,
    { isLoading: isDeleting, isError: errorDeletingPractice, data },
  ] = useDeletePracticeMutation();

  const { filtering, sorting } = useSelector((state: RootState) => ({
    filtering: state.practiceManagementFilters.filtering,
    sorting: state.practiceManagementFilters.sorting,
  }));

  const {
    data: practices,
    isFetching,
    isError,
  } = useGetPracticesQuery({
    filtering: {
      search_term: filtering.search_term,
      status: filtering.status,
    },
    sorting: {
      sort_by: sorting.sort_by,
    },
    includeLocations: true,
  });

  const { control, watch, getValues } = useForm<Inputs>();

  useEffect(() => {
    if (data !== undefined) {
      setDeletingPractice(undefined);
      notify(
        <NotifierNotification
          title="Success!"
          message="Practice has been offboarded"
        />,
      );
    }
  }, [data]);

  useEffect(() => {
    if (errorDeletingPractice) {
      setDeletingPractice(undefined);
      notify(
        <NotifierNotification
          type="error"
          title="Error"
          message="Unable to offboard this practice. Please try again later."
        />,
      );
    }
  }, [errorDeletingPractice]);

  const triggerUpdate = (id: number, value: boolean) => {
    const practice = practices?.find((p) => p.id === id)!;
    updatePractice({
      id,
      body: {
        merlin_id: practice.merlin_id!,
        subdomain: practice.subdomain,
        is_active: value,
        locations: practice.locations
          ?.filter((l) => l.enabled)
          .map((l) => l.id),
      },
    });
  };

  const searchParams = useMemo(
    () => ({
      search: debouncedSearchTerm,
      status: statusFilter,
      sort: sort,
    }),
    [debouncedSearchTerm, sort, statusFilter],
  );

  useSearchQuery(searchParams);

  useEffect(() => {
    if (filtering.search_term !== debouncedSearchTerm) {
      dispatch(setFilters({ search_term: debouncedSearchTerm }));
    }

    if (filtering.status !== statusFilter) {
      dispatch(setFilters({ status: statusFilter }));
    }

    if (sorting.sort_by !== sort) {
      dispatch(setSorting({ sort_by: sort }));
    }
  }, [debouncedSearchTerm, sort, statusFilter]);

  const sortByOptions = [
    { id: "default", name: "No sort" },
    { id: "name-asc", name: "Name A-Z" },
    { id: "name-desc", name: "Name Z-A" },
  ];

  const statusFilterOptions = [
    { id: "activated", display: "Active" },
    { id: "deactivated", display: "Disabled" },
  ];

  useEffect(() => {
    if (getValues("status") === undefined && statusFilter) {
      setStatusFilter(undefined);
    }
  }, [watch("status")]);

  return (
    <>
      <div className="mb-8">
        <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 gap-x-6">
          {/* search */}
          <div className="tablet:col-span-2 desktop:col-span-3">
            <Input
              size="small"
              prefixIcon={<SearchIcon />}
              placeHolder="Search for vet practices"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="col-start-4 tablet:col-start-5 tablet:col-end-7 desktop:col-start-7 desktop:col-end-9">
            <Select
              name="sort_by"
              size="small"
              control={control}
              prefixIcon={<SortIcon />}
              placeholder="Sort"
              onChange={(value) =>
                setSort(value === "default" ? undefined : (value as SortBy))
              }
            >
              {sortByOptions?.map((s) => (
                <SelectOption key={s.id} value={s.id}>
                  {s.name}
                </SelectOption>
              ))}
            </Select>
          </div>

          <div className="col-start-5 tablet:col-start-7 tablet:col-end-9 desktop:col-end-11 desktop:col-start-9">
            <Filter
              size="small"
              buttonText="Filter"
              prefixIcon={<FilterIcon />}
            >
              {({ close }) => {
                return (
                  <>
                    <Filter.Section>
                      <Filter.Item
                        name="status"
                        control={control}
                        title="Status"
                        radioNames={statusFilterOptions}
                      />
                    </Filter.Section>
                    <Filter.Section>
                      <Button
                        block
                        size="small"
                        disabled={watch("status") === statusFilter}
                        onClick={() => {
                          setStatusFilter(getValues("status"));
                          close();
                        }}
                      >
                        Apply
                      </Button>
                    </Filter.Section>
                  </>
                );
              }}
            </Filter>
          </div>
        </div>
      </div>

      <Table loading={isFetching || isDeleting}>
        <Table.Head>
          <Table.Row>
            <Table.Item as="th">Vet Practice</Table.Item>
            <Table.Item as="th" fullWidth>
              Locations
            </Table.Item>
            <Table.Item as="th">Active</Table.Item>
            <Table.Item as="th">Edit</Table.Item>
            <Table.Item as="th">Delete</Table.Item>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {isError ? (
            <Table.Row>
              <Table.Item colSpan={5} className="text-center">
                <ErrorMessage message="Unable to fetch a list of practices" />
              </Table.Item>
            </Table.Row>
          ) : practices?.length ? (
            practices.map((practice) => {
              const enabledLocations = practice.locations?.filter(
                (l) => l.enabled,
              );
              return (
                <Table.Row key={practice.id}>
                  <Table.Item>{practice.display_name}</Table.Item>
                  <Table.Item className="flex flex-wrap items-center h-full gap-2">
                    {enabledLocations?.length === 0 && (
                      <p className="text-sm font-semibold text-grey-dark">
                        No enabled locations
                      </p>
                    )}
                    {enabledLocations
                      ?.slice(0, 5)
                      .map((location) => (
                        <Tag key={location.id}>{location.name}</Tag>
                      ))}
                    {enabledLocations && enabledLocations?.length > 5 && (
                      <p className="text-sm font-semibold text-grey-dark">
                        + {enabledLocations?.length - 5} more
                      </p>
                    )}
                  </Table.Item>
                  <Table.Item className="text-center">
                    <Toggle
                      value={practice.is_active}
                      onChange={(v) => triggerUpdate(practice.id, v)}
                    />
                  </Table.Item>
                  <Table.Item className="text-center">
                    <div
                      className="inline-block cursor-pointer"
                      onClick={() => setEditingPractice(practice.id)}
                    >
                      <PencilIcon className="w-6 h-6 text-primary" />
                    </div>
                  </Table.Item>
                  <Table.Item className="text-center">
                    <div
                      className="inline-block cursor-pointer"
                      onClick={() => setDeletingPractice(practice.id)}
                    >
                      <TrashIcon className="w-6 h-6 text-error" />
                    </div>
                  </Table.Item>
                </Table.Row>
              );
            })
          ) : (
            <Table.Row>
              <Table.Item colSpan={5} className="text-center">
                No practices found
              </Table.Item>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      <p
        className="mt-6 font-semibold cursor-pointer text-primary"
        onClick={() => setShowNewPracticeModal(true)}
      >
        + Add new practice
      </p>

      <NewPracticeModal
        isOpen={showNewPracticeModal}
        onClose={() => setShowNewPracticeModal(false)}
      />

      <EditPracticeModal
        isOpen={editingPractice !== undefined}
        onClose={() => setEditingPractice(undefined)}
        practiceId={editingPractice}
      />

      <ConfirmationModal
        isOpen={deletingPractice !== undefined}
        onClose={() => setDeletingPractice(undefined)}
        danger
        onConfirm={() => deletePractice(deletingPractice!)}
        title="Delete Practice"
        confirmText="Delete"
        confirmLoading={isDeleting}
        description={
          <>
            <p>
              Are you sure you want to delete the practice{" "}
              <strong>
                {
                  practices?.find((p) => p.id === deletingPractice)
                    ?.display_name
                }
                ?
              </strong>
            </p>
            <p>This will not remove the practice from Merlin.</p>
          </>
        }
      />
    </>
  );
};

export default PracticesPage;
